import { CommonModule, Location, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject, Input, SimpleChanges } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { RelService } from '@df/core/meta/router/rel.service';
import { DfEventService, IEventType } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { MultisiteService } from '@df/core/service/multisite';
import { TimeoutService } from '@df/ng/timeout.service';
import { NUi } from '@df/ui/df-ui.config';
import { Subscription } from 'rxjs';

type ThrefAction = {
    action: string[];
};

type ThrefUrl = {
    href: string;
    path: string;
    fragment: string | undefined;
    queryParams: Params | null;
    target: TTarget | undefined;
    rel?: string;
    fullRedirect: boolean;
};

type TTarget = '_self' | '_top' | '_blank';

declare module '@df/core/service/df-event' {
    interface IEventType {
        // basket
        hrefClick: DfEventConfig<
            'hrefClick',
            {
                finalUrl: ThrefUrl;
                clickEvent: MouseEvent | TouchEvent;
            },
            void
        >;
    }
}

declare global {
    export interface Event {
        preventOptHref: boolean;
    }
}

@Component({
    selector: 'href',
    templateUrl: './href.html',
    standalone: true,
    imports: [CommonModule, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HrefComponent {
    /**
     * Link or action definition
     */
    @Input()
    link: string | undefined | null;

    /**
     * Optional class name to pass `a` tag.
     */
    @Input()
    hrefClass?: string;

    /**
     * Optional target to pass to `a` tag. If `_self` then *reload* the page.
     */
    @Input()
    hrefTarget?: TTarget;

    /**
     * Aria label
     */
    @Input()
    hrefAriaLabel?: string;

    /**
     * When location is the same as href then add class
     */
    @Input()
    hrefCurrentClass?: string;

    /**
     * Tab index
     */
    @Input()
    hrefTabIndex = 0;

    /**
     * In this mode <a> or <button> is not created, only the content is rendered
     * Especially needed when nesting hrefs not to kill universal
     */
    @Input()
    plain = false;

    /**
     * Generated object with information about the link
     */
    protected finalUrl?: ThrefUrl;

    /**
     * Generated object with information about the action
     */
    protected finalAction?: ThrefAction;

    /**
     * Elements class name
     */
    protected className?: string;

    /**
     * Services
     */
    private router = inject(Router);
    private location = inject(Location);
    private dfEventService = inject(DfEventService);
    private events = inject(Events);
    private relService = inject(RelService);
    private timeoutService = inject(TimeoutService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private multisiteService = inject(MultisiteService);
    private uiConfig = inject(NUi.OPTIONS);

    /**
     * Supporting variable
     */
    private currentClassSubscription?: Subscription;
    private timeoutId?: number;

    /**
     * Changes hook
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes['hrefTarget'] || changes['href'] || changes['link']) {
            const link = this.generateLink();
            const isAction = link && 'action' in link;

            if (isAction) {
                this.finalAction = link as ThrefAction;
                this.finalUrl = undefined;
            } else {
                this.finalAction = undefined;
                this.finalUrl = link as ThrefUrl;

                if (link) {
                    this.finalUrl.rel = this.relService.getRel(this.finalUrl.href);
                }
            }
        }
        if (changes['hrefCurrentClass']) {
            if (this.hrefCurrentClass) {
                if (!this.currentClassSubscription) {
                    this.currentClassSubscription = this.router.events.subscribe(event => {
                        if (event instanceof NavigationEnd) {
                            this.updateCurrentClass();
                        }
                    });
                }
            } else if (this.currentClassSubscription) {
                this.currentClassSubscription.unsubscribe();
            }
        }

        this.updateCurrentClass();
    }

    ngOnDestroy() {
        this.currentClassSubscription?.unsubscribe();
    }

    /**
     * On click action
     * @param event
     */
    @HostListener('dblclick', ['$event'])
    @HostListener('click', ['$event'])
    handleClick(event: MouseEvent) {
        // do nothing for ctrl/cmd + click
        if (event.ctrlKey || event.metaKey) {
            return;
        }

        // in case its double click
        clearTimeout(this.timeoutId);

        // if there is a flag not to continue then respect it
        if (event.preventOptHref) {
            event.preventDefault();
            return;
        }

        if (this.finalAction) {
            const action = this.finalAction.action;
            const eventData = action.slice(2);
            this.events.broadcast(action[1], eventData.length === 1 ? eventData[0] : eventData);
        } else if (this.finalUrl) {
            if (!this.finalUrl.fullRedirect && !event.defaultPrevented && !event.preventOptHref) {
                // some elements may want to block this action so lets give them a moment
                this.timeoutId = this.timeoutService.setTimeout(() => {
                    this.dfEventService.dispatch({
                        id: 'hrefClick',
                        data: {
                            finalUrl: this.finalUrl!,
                            clickEvent: event
                        },
                        action: (clickEventData: IEventType['hrefClick']['data']) => {
                            // if is not prevented by first click in zippy
                            if (!clickEventData.clickEvent.preventOptHref) {
                                const urlTree = this.router.createUrlTree([this.finalUrl!.path], {
                                    fragment: this.finalUrl!.fragment,
                                    queryParams: this.finalUrl!.queryParams
                                });
                                const href = urlTree.toString();
                                const isSamePage = this.stripHash(href) === this.stripHash(this.router.url);

                                // Reload page
                                if (this.uiConfig.href.reloadPage && isSamePage) {
                                    // Change hash before reload
                                    if (href.includes('#')) {
                                        window.location.hash = href.split('#')[1];
                                    }
                                    // reload
                                    window.location.reload();
                                } else {
                                    const onSameUrlNavigation =
                                        (!clickEventData.finalUrl.target || clickEventData.finalUrl.target === '_self') && isSamePage
                                            ? 'reload'
                                            : 'ignore';

                                    this.timeoutService.ngZone.run(() => {
                                        this.router.navigateByUrl(urlTree, { onSameUrlNavigation });
                                    });
                                }
                            }
                        }
                    });
                }, 20);
            }

            if (this.finalUrl.fullRedirect) {
                return true;
            } else {
                event.preventDefault();
            }
        }
    }

    private updateCurrentClass() {
        const useCurrentClass = this.hrefCurrentClass && this.finalUrl ? this.finalUrl.href === this.location.path() : false;
        const className = [this.hrefClass, useCurrentClass ? this.hrefCurrentClass : undefined].filter(Boolean).join(' ');

        if (this.className !== className) {
            this.className = className;
            this.detectChanges();
        }
    }

    private generateLink(): ThrefAction | Omit<ThrefUrl, 'rel'> | undefined {
        const link = this.link;

        if (typeof link === 'string' && link.length > 0) {
            let [fragmentless, fragment] = link.split('#');
            const [path, paramsString] = fragmentless.split('?');

            // ng will encode it back
            if (fragment) {
                fragment = decodeURIComponent(fragment);
            }

            if (path.includes(':')) {
                const linkParts = link.split(':');

                switch (linkParts[0]) {
                    case 'event':
                        return {
                            action: linkParts
                        };
                    case 'http':
                    case 'https':
                        return {
                            href: link,
                            path,
                            fragment: undefined, // no need for external ones, it can be in href
                            queryParams: null,
                            target: typeof this.hrefTarget === 'string' ? this.hrefTarget : '_blank',
                            fullRedirect: true
                        };
                    default:
                        return {
                            href: link,
                            path,
                            fragment,
                            queryParams: null,
                            target: typeof this.hrefTarget === 'string' ? this.hrefTarget : undefined,
                            fullRedirect: true
                        };
                }
            }

            const queryParams = paramsString ? Object.fromEntries(new URLSearchParams(paramsString).entries()) : null;

            // if link contains a dot (so it is a file) then make sure new tab is used
            // but only if hrefTarget is not explicitly set
            const target = typeof this.hrefTarget === 'string' ? this.hrefTarget : path.includes('.') ? '_blank' : undefined;

            return {
                href: ((this.multisiteService.base || '') + link).replace('//', '/'),
                path,
                fragment,
                queryParams,
                target,
                fullRedirect: target === '_blank'
            };
        }
    }

    private stripHash(url: string): string {
        return url.split('#')[0];
    }

    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
